import { useEffect, useMemo, useState } from 'react';
import { useOrg } from '@/features/team/OrgProvider';
import { ClientPicker } from '@/features/clients/ClientPicker';
import { EstablishmentPicker } from '@/features/clients/EstablishmentPicker';
import { CampoNumero } from '@/ui/CampoNumero';
import { useBotonAtras } from '@/core/android/atras';
import { Chip } from '@/ui/Chips';
import { RepartoFactura } from '@/ui/RepartoFactura';
import {
  fetchClientInvoiceRecipients,
  fetchWorkInvoiceRecipients,
  setWorkInvoiceRecipients,
  type DestinatarioFactura,
} from '@/features/clients/clients';
import { Plegable } from '@/ui/Plegable';
import { esErrorDeRed, mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useBundleOpcional } from '@/core/offline/BundleProvider';
import { clavePeriodo, defaultsDelBundle, destinatariosDelBundle } from '@/core/offline/bundle';
import { Boton, EncabezadoPantalla, EncabezadoSeccion } from '@/ui/Sistema';
import type { Client, Establishment, MobilityMode } from '@/features/clients/clients';
import { fetchOrganizationSettings, MOBILITY_LABELS } from '@/features/clients/clients';
import {
  formatCents,
  listActiveServices,
  MESES,
  parsePesosToCents,
  resolvePrice,
  type OrganizationService,
} from '@/features/catalog/catalog';
import { allocateCents, buildEvenSplit } from '@/core/economics';
import { ServicePicker } from './ServicePicker';
import { registrarReciente } from './recientes';
import { useCola } from '@/core/offline/ColaProvider';
import {
  fetchWorkDefaults,
  fetchWorkForEdit,
  fromSplitLines,
  listParticipants,
  listSplitTemplates,
  periodIsClosed,
  toSplitLines,
  updateWork,
  type MobilityDraft,
  type Participant,
  type SplitDraft,
  type SplitTemplate,
  type WorkDefaults,
  type WorkItemDraft,
  type WorkPayload,
} from './works';

const hoy = new Date();
const MODES: MobilityMode[] = ['none', 'per_km_fuel', 'per_km_fixed', 'flat', 'manual'];

let contador = 0;
const nuevaClave = () => `linea-${++contador}`;

/** Redondeo a 3 decimales, igual que `numeric(12,3)` en la base. */
const kg3 = (n: number) => Math.round(n * 1000) / 1000;

const hoyISO = () => new Date().toISOString().slice(0, 10);

export type WorkFormMode = 'create' | 'edit' | 'duplicate';

interface Props {
  /** `edit` reescribe el trabajo; `duplicate` crea uno nuevo a partir de otro. */
  mode?: WorkFormMode;
  /** Trabajo de origen. Obligatorio en `edit` y `duplicate`. */
  workId?: string | undefined;
  onSaved: (resultado: ResultadoGuardado) => void;
  onCancel: () => void;
}

const TITULOS: Record<WorkFormMode, string> = {
  create: 'Nuevo trabajo',
  edit: 'Editar trabajo',
  duplicate: 'Duplicar trabajo',
};

/**
 * Cómo terminó el alta.
 *
 * Existe para que la pantalla de al lado no pueda equivocarse: no hay forma de
 * decir "guardado" sin haber recibido un `workId`, porque en el otro caso no
 * hay ninguno que mostrar.
 */
export type ResultadoGuardado =
  { estado: 'sincronizado'; workId: string } | { estado: 'pendiente' };

export function WorkForm({ mode = 'create', workId, onSaved, onCancel }: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  const { encolar, hayConexion } = useCola();
  const esEdicion = mode === 'edit';
  const esDuplicado = mode === 'duplicate';

  // --- Generales
  const [workDate, setWorkDate] = useState(hoyISO());
  // El mes de facturación SIGUE a la fecha del trabajo hasta que el usuario lo
  // cambia a mano. Es estado derivado, no un efecto que dispare renders.
  const [billingOverride, setBillingOverride] = useState<{ year: number; month: number } | null>(
    null,
  );
  const [client, setClient] = useState<Client | null>(null);
  const [establishment, setEstablishment] = useState<Establishment | null>(null);

  // --- Catálogo y participantes
  const [services, setServices] = useState<OrganizationService[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [templates, setTemplates] = useState<SplitTemplate[]>([]);
  const [steerPeriodo, setSteerPeriodo] = useState<number | null>(null);
  const [fuelPeriodo, setFuelPeriodo] = useState<number | null>(null);
  // Un trabajo congelado conserva los precios con los que se guardó (Spec §14).
  const [precioCongelado, setPrecioCongelado] = useState<{
    steer: number | null;
    fuel: number | null;
  } | null>(null);

  // --- Honorarios
  const [items, setItems] = useState<WorkItemDraft[]>([]);
  // Sólo el selector de la última línea agregada arranca abierto.
  const [lineaNueva, setLineaNueva] = useState<string | null>(null);
  // Método de movilidad habitual de la organización. No se codifica ninguno:
  // cada veterinaria cobra como cobra, y suponer «km × combustible» sería
  // ponerle a todas la costumbre de una.
  const [modoHabitual, setModoHabitual] = useState<MobilityMode | null>(null);
  const [otrosMetodos, setOtrosMetodos] = useState(false);

  // --- Movilidad
  const [mobility, setMobility] = useState<MobilityDraft>({
    mode: 'none',
    km: null,
    rate_cents: null,
    amount_cents: null,
    allocations: [],
  });
  const [mobBeneficiario, setMobBeneficiario] = useState<string>('');

  // --- Economía
  const [biller, setBiller] = useState<string>('');
  const [splits, setSplits] = useState<SplitDraft[]>([]);
  const [repartoManual, setRepartoManual] = useState(false);

  // --- Administración
  const [isBilled, setIsBilled] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [description, setDescription] = useState('');
  // A nombre de quién se emite la factura. Se hereda del cliente al elegirlo y
  // se CONGELA en el trabajo al guardar: cambiar mañana la configuración del
  // cliente no puede reescribir a quién se le facturó hace tres meses.
  const [destinatarios, setDestinatarios] = useState<DestinatarioFactura[]>([]);
  const [bloqueado, setBloqueado] = useState(false);
  // El período de FACTURACIÓN elegido está cerrado: la base va a rechazar
  // el guardado, así que conviene decirlo antes y no después.
  const [periodoCerrado, setPeriodoCerrado] = useState(false);

  const [error, setError] = useState<string | null>(null);
  // Si hay servidor, y la copia operativa por si no lo hay.
  const sinServidor = useSinConexion();
  /** No se pudieron traer los valores habituales de este cliente. */
  const [faltanDefaults, setFaltanDefaults] = useState(false);
  const { bundle } = useBundleOpcional() ?? { bundle: null };
  const [guardando, setGuardando] = useState(false);
  const [cargandoTrabajo, setCargandoTrabajo] = useState(Boolean(workId));
  // Los defaults heredados no deben pisar lo que vino del trabajo original.
  const [trabajoCargado, setTrabajoCargado] = useState(false);

  const fechaTrabajo = new Date(`${workDate}T00:00:00`);
  const billingYear =
    billingOverride?.year ??
    (Number.isNaN(fechaTrabajo.getTime()) ? hoy.getFullYear() : fechaTrabajo.getFullYear());
  const billingMonth =
    billingOverride?.month ??
    (Number.isNaN(fechaTrabajo.getTime()) ? hoy.getMonth() + 1 : fechaTrabajo.getMonth() + 1);

  const soloUno = participants.length === 1;

  // Precio efectivo: el congelado manda sobre el del período.
  const steerPrice = precioCongelado ? precioCongelado.steer : steerPeriodo;
  const fuelPrice = precioCongelado ? precioCongelado.fuel : fuelPeriodo;

  // ---------------------------------------------------------------------------
  // Carga: catálogo, participantes, plantillas y precios del período
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        // Sin servidor, todo esto sale de la copia operativa. Antes se pedía
        // igual y fallaba: el formulario abría pero sin honorarios, sin
        // participantes y sin precios, o sea sin nada con qué cargar el
        // trabajo. Que abriera era peor que no abrir, porque parecía que se
        // podía.
        if (sinServidor) {
          if (cancelado) return;
          const p = bundle?.precios[clavePeriodo(billingYear, billingMonth)];
          const prtLocal = bundle?.participantes ?? [];
          setServices(bundle?.servicios ?? []);
          setParticipants(prtLocal);
          setTemplates(bundle?.repartos ?? []);
          setSteerPeriodo(p?.steer ?? null);
          setFuelPeriodo(p?.fuel ?? null);
          if (prtLocal.length === 1 && prtLocal[0]) {
            setBiller((b) => b || prtLocal[0]!.id);
            setSplits((x) => (x.length ? x : fromSplitLines(buildEvenSplit([prtLocal[0]!.id]))));
            setMobBeneficiario((m) => m || prtLocal[0]!.id);
          }
          return;
        }

        // Se resuelven por separado: si falla la consulta de precios, el
        // formulario debe seguir usable con sus honorarios y participantes.
        const [srvR, prtR, tplR, steerR, fuelR] = await Promise.allSettled([
          listActiveServices(orgId),
          listParticipants(orgId),
          listSplitTemplates(orgId),
          resolvePrice(orgId, 'steer', billingYear, billingMonth),
          resolvePrice(orgId, 'fuel', billingYear, billingMonth),
        ]);
        if (cancelado) return;

        const srv = srvR.status === 'fulfilled' ? srvR.value : [];
        const prt = prtR.status === 'fulfilled' ? prtR.value : [];
        setServices(srv);
        setParticipants(prt);
        setTemplates(tplR.status === 'fulfilled' ? tplR.value : []);
        setSteerPeriodo(steerR.status === 'fulfilled' ? steerR.value : null);
        setFuelPeriodo(fuelR.status === 'fulfilled' ? fuelR.value : null);

        const fallo = [srvR, prtR, steerR, fuelR].find((r) => r.status === 'rejected');
        if (fallo && fallo.status === 'rejected') {
          // Si lo que falló fue la red, no se muestra un error: se completa con
          // la copia local y se sigue. La conectividad ya quedó marcada, así
          // que el próximo render entra por el camino de arriba.
          if (esErrorDeRed(fallo.reason)) {
            const p = bundle?.precios[clavePeriodo(billingYear, billingMonth)];
            if (srv.length === 0) setServices(bundle?.servicios ?? []);
            if (prt.length === 0) setParticipants(bundle?.participantes ?? []);
            setSteerPeriodo((v) => v ?? p?.steer ?? null);
            setFuelPeriodo((v) => v ?? p?.fuel ?? null);
          } else {
            setError(mensajeDeError(fallo.reason));
          }
        }

        // Veterinario solo: no le preguntamos lo que ya sabemos
        if (prt.length === 1 && prt[0]) {
          setBiller((b) => b || prt[0]!.id);
          setSplits((s) => (s.length ? s : fromSplitLines(buildEvenSplit([prt[0]!.id]))));
          setMobBeneficiario((m) => m || prt[0]!.id);
        }
      } catch (e) {
        if (!cancelado && !esErrorDeRed(e)) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, billingYear, billingMonth, sinServidor, bundle]);

  // ---------------------------------------------------------------------------
  // ¿El mes de facturación elegido está cerrado?
  // ---------------------------------------------------------------------------
  // Sólo importa para lo que va a ESCRIBIR en ese mes. Un trabajo que ya está
  // bloqueado se avisa por otro lado (`bloqueado`), y el mes no se corrige
  // solo: la decisión de reabrir el período es del administrador.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const cerrado = await periodIsClosed(orgId, billingYear, billingMonth);
        if (!cancelado) setPeriodoCerrado(cerrado);
      } catch {
        // Si la consulta falla no se bloquea nada: la base sigue siendo la que
        // rechaza de verdad, y adivinar acá sería peor que no avisar.
        if (!cancelado) setPeriodoCerrado(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, billingYear, billingMonth]);

  // ---------------------------------------------------------------------------
  // Carga del trabajo de origen (editar o duplicar)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!workId) return;
    let cancelado = false;
    void (async () => {
      try {
        const w = await fetchWorkForEdit(workId);
        if (cancelado) return;

        setClient({ id: w.client_id, name: w.client_name } as Client);
        if (w.establishment_id) {
          setEstablishment({
            id: w.establishment_id,
            name: w.establishment_name ?? '',
          } as Establishment);
        }

        // DUPLICAR crea un trabajo NUEVO: fecha de hoy, período actual, sin
        // estado administrativo heredado. EDITAR conserva todo tal cual.
        if (esDuplicado) {
          setWorkDate(hoyISO());
          setBillingOverride(null);
          setIsBilled(false);
          setIsPaid(false);
        } else {
          setWorkDate(w.work_date);
          setBillingOverride({ year: w.billing_year, month: w.billing_month });
          setIsBilled(w.is_billed);
          setIsPaid(w.is_paid);
          setBloqueado(w.is_locked);
          // Sólo al EDITAR se respetan los precios congelados: el duplicado es
          // un trabajo nuevo y se valoriza con el precio de su propio período.
          if (w.price_frozen) {
            setPrecioCongelado({ steer: w.steer_price_cents, fuel: w.fuel_price_cents });
          }
        }

        setBiller(w.biller_participant_id);
        setDescription(w.description ?? '');
        // Los del TRABAJO, no los del cliente: son los que estaban vigentes
        // cuando se cargó, y son los que explican la factura que se emitió.
        // En un duplicado se heredan igual, porque es un trabajo nuevo.
        try {
          const dest = await fetchWorkInvoiceRecipients(workId);
          if (!cancelado) setDestinatarios(dest);
        } catch {
          // Sin destinatarios el trabajo se factura entero al cliente.
        }
        setSplits(w.splits);
        if (w.splits.length > 0) setRepartoManual(true);

        if (w.mobility) {
          setMobility({
            mode: w.mobility.mode,
            km: w.mobility.km,
            rate_cents: w.mobility.rate_cents,
            amount_cents: w.mobility.amount_cents,
            allocations: w.mobility.allocations ?? [],
          });
          const primero = (w.mobility.allocations ?? [])[0];
          if (primero) setMobBeneficiario(primero.participant_id);
        }

        // Los kg se COPIAN del original. `kg_suggested` trae el valor VIGENTE
        // del tarifario, así la línea se marca sola cuando difieren y el
        // usuario decide caso por caso: no se pierden tarifas excepcionales ni
        // se arrastran valores viejos en silencio.
        setItems(
          w.items.map((i) => ({
            key: nuevaClave(),
            organization_service_id: i.organization_service_id,
            service_name: i.service_name,
            calculation: i.calculation,
            kg_suggested: i.kg_current ?? i.kg_per_unit,
            kg_per_unit: i.kg_per_unit,
            quantity: i.quantity,
          })),
        );

        setTrabajoCargado(true);
        setCargandoTrabajo(false);
      } catch (e) {
        if (!cancelado) {
          setError(mensajeDeError(e));
          setCargandoTrabajo(false);
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [workId, esDuplicado]);

  // El método habitual se lee de la organización al abrir el formulario, para
  // poder ofrecerlo como atajo antes de que se elija un cliente.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        if (sinServidor) {
          if (!cancelado && bundle?.ajustes) setModoHabitual(bundle.ajustes.default_mobility_mode);
          return;
        }
        const s = await fetchOrganizationSettings(orgId);
        if (!cancelado && s) setModoHabitual(s.default_mobility_mode);
      } catch {
        // Sin el ajuste el formulario funciona igual: se muestran los métodos
        // sin destacar ninguno.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  // ---------------------------------------------------------------------------
  // Defaults heredados al elegir cliente/establecimiento
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!client) return;
    // Un trabajo cargado ya trae sus propios valores: heredar acá los pisaría.
    if (workId && !trabajoCargado) return;
    if (workId) return;
    let cancelado = false;

    // Heredar es lo mismo con señal y sin ella: cambia de dónde salen los
    // valores, no qué se hace con ellos. Por eso una sola función, aplicada
    // sobre lo que haya contestado el servidor o sobre lo que quedó guardado
    // en el teléfono en el último sync.
    const aplicar = (d: WorkDefaults, dest: DestinatarioFactura[]) => {
      setMobility((m) => ({
        ...m,
        mode: d.charge_mobility === false ? 'none' : (d.mobility_mode ?? m.mode),
        km: d.km ?? m.km,
        rate_cents: d.mobility_rate_cents ?? m.rate_cents,
        amount_cents: d.flat_mobility_cents ?? m.amount_cents,
      }));
      if (d.mobility_participant_id) setMobBeneficiario((v) => v || d.mobility_participant_id!);
      if (d.biller_participant_id) setBiller((v) => v || d.biller_participant_id!);
      // El reparto de factura se hereda sólo si todavía no hay uno: si ya se
      // tocó a mano para este trabajo, gana lo cargado.
      setDestinatarios((v) => (v.length ? v : dest));
      if (d.split_template_id) {
        setSplits((s) => {
          if (s.length) return s;
          const tpl = templates.find((t) => t.id === d.split_template_id);
          return tpl ? tpl.lines : s;
        });
      }
    };

    // Sin servidor, de la copia operativa. Los valores habituales viajan en
    // ella desde que el servidor los devuelve todos juntos, así que elegir un
    // cliente sin señal hereda lo mismo que heredaría con señal —que es el
    // punto: estos valores existen para no equivocar la movilidad, el
    // facturador y el reparto, y es sin señal cuando menos se puede revisar—.
    const local = () => {
      const d = defaultsDelBundle(bundle, client.id, establishment?.id ?? null);
      if (!d) {
        // Un cliente que no estaba en el último sync. Se dice, en vez de dejar
        // los campos vacíos como si no tuviera valores habituales.
        setFaltanDefaults(true);
        return;
      }
      setFaltanDefaults(false);
      aplicar(d, destinatariosDelBundle(bundle, client.id));
    };

    void (async () => {
      if (sinServidor) {
        if (!cancelado) local();
        return;
      }
      try {
        const [d, dest] = await Promise.all([
          fetchWorkDefaults(client.id, establishment?.id ?? null),
          fetchClientInvoiceRecipients(client.id),
        ]);
        if (cancelado) return;
        setFaltanDefaults(false);
        aplicar(d, dest);
      } catch (e) {
        if (cancelado) return;
        // Se cayó la señal entre que se abrió el formulario y se eligió el
        // cliente: la copia local sigue estando y sirve igual.
        if (esErrorDeRed(e)) local();
        else setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, establishment, workId, trabajoCargado, templates, sinServidor, bundle]);

  // ---------------------------------------------------------------------------
  // Cálculo en vivo — mismo motor que usa el servidor
  // ---------------------------------------------------------------------------
  const totalKg = useMemo(
    () =>
      kg3(
        items.reduce(
          (acc, i) =>
            acc +
            (i.calculation === 'per_job'
              ? kg3(i.kg_per_unit ?? 0)
              : kg3((i.quantity ?? 0) * (i.kg_per_unit ?? 0))),
          0,
        ),
      ),
    [items],
  );

  const honorariaCents = useMemo(
    () => (steerPrice === null ? null : Math.round((totalKg * 1000 * steerPrice) / 1000)),
    [totalKg, steerPrice],
  );

  const mobilityCents = useMemo((): number | null => {
    if (mobility.mode === 'none') return 0;
    if (mobility.mode === 'flat' || mobility.mode === 'manual') return mobility.amount_cents ?? 0;
    const km = mobility.km ?? 0;
    if (km === 0) return 0;
    const rate = mobility.mode === 'per_km_fuel' ? fuelPrice : mobility.rate_cents;
    if (rate === null || rate === undefined) return null;
    return Math.round(km * rate);
  }, [mobility, fuelPrice]);

  const totalCents =
    honorariaCents === null || mobilityCents === null ? null : honorariaCents + mobilityCents;

  const sumaSplits = splits.reduce((a, s) => a + Math.round(s.percentage * 10000), 0);
  const splitsValidos = sumaSplits === 1_000_000;

  const puedeCargar = !periodoCerrado || bloqueado;

  // ¿Hay algo cargado que se perdería al salir?
  //
  // Sólo cuenta como «cargado» lo que la persona escribió: la fecha viene
  // puesta sola y el mes también, así que un formulario recién abierto no debe
  // preguntar nada. Preguntar de más enseña a contestar que sí sin leer.
  const hayAlgoCargado =
    client !== null || items.length > 0 || (mobility.km ?? 0) > 0 || description.trim() !== '';

  // Atrás con un trabajo a medio cargar pregunta antes de descartarlo. En un
  // teléfono, atrás se toca por reflejo para cerrar cualquier cosa; sin esto,
  // ese reflejo borra el trabajo de una visita entera sin decir nada.
  useBotonAtras(() => {
    if (!hayAlgoCargado) return false;
    if (confirm('¿Descartar este trabajo? Lo que cargaste se pierde.')) onCancel();
    return true;
  });

  const repartoPreview = useMemo(() => {
    if (honorariaCents === null || !splitsValidos || splits.length === 0) return null;
    try {
      return allocateCents(honorariaCents, toSplitLines(splits));
    } catch {
      return null;
    }
  }, [honorariaCents, splits, splitsValidos]);

  const nombreDe = (id: string) => participants.find((p) => p.id === id)?.display_name ?? '—';

  // Lo que cada sección plegada dice de sí misma. Es la parte que hace que
  // plegar no sea esconder: se lee el estado sin abrir nada.
  const resumenMovilidad =
    mobility.mode === 'none'
      ? 'No se cobra'
      : [
          MOBILITY_LABELS[mobility.mode],
          mobility.km != null ? `${mobility.km} km` : null,
          formatCents(mobilityCents),
        ]
          .filter(Boolean)
          .join(' · ');

  // Honorarios plegado tiene que decir QUÉ se hizo y cuánto, que es lo que uno
  // vuelve a mirar. Con «3 líneas» habría que abrir para saber si son las que
  // corresponden.
  const resumenHonorarios =
    items.length === 0
      ? 'Sin honorarios todavía'
      : items
          .filter((i) => i.service_name)
          .map((i) => `${i.service_name} · ${i.quantity ?? 0}`)
          .join(' · ') || `${items.length} línea${items.length === 1 ? '' : 's'} sin completar`;

  const resumenEconomia = [
    biller ? `Factura ${nombreDe(biller)}` : 'Falta quién factura',
    splits.length === 0
      ? 'sin reparto'
      : splits.map((x) => `${nombreDe(x.participant_id)} ${x.percentage} %`).join(' · '),
  ].join(' · ');

  const lineasIncompletas = items.filter((i) => !i.service_name).length;
  const lineasModificadas = items.filter(
    (i) => i.service_name && kg3(i.kg_per_unit ?? 0) !== kg3(i.kg_suggested),
  ).length;

  // ---------------------------------------------------------------------------
  // Acciones
  // ---------------------------------------------------------------------------
  // Una línea nueva nace SIN honorario elegido. Ver ServicePicker.
  const agregarLinea = () => {
    const key = nuevaClave();
    setLineaNueva(key);
    setItems((prev) => [
      ...prev,
      {
        key,
        organization_service_id: null,
        service_name: '',
        calculation: 'per_unit',
        kg_suggested: 0,
        // Nacen vacíos: el campo pide un número, no propone uno.
        kg_per_unit: null,
        quantity: null,
      },
    ]);
  };

  const cambiarServicio = (key: string, s: OrganizationService) => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === key
          ? {
              ...i,
              organization_service_id: s.id,
              service_name: s.name,
              calculation: s.calculation,
              kg_suggested: s.kg_novillo,
              kg_per_unit: s.kg_novillo,
            }
          : i,
      ),
    );
  };

  // --- Acciones rápidas de reparto -------------------------------------------
  // Todas pasan por `buildEvenSplit` o por porcentajes exactos: el usuario NUNCA
  // tiene que resolver a mano el resto que deja una división inexacta.
  const repartoParaTodos = () => {
    const ids = participants.map((p) => p.id);
    if (ids.length) setSplits(fromSplitLines(buildEvenSplit(ids)));
  };

  const repartoParaUno = (participantId: string) => {
    setSplits(fromSplitLines(buildEvenSplit([participantId])));
  };

  const aplicarPlantilla = (t: SplitTemplate) => {
    setSplits(t.lines.map((l) => ({ ...l })));
  };

  const repartoActualEs = (candidato: SplitDraft[]) => {
    if (candidato.length !== splits.length) return false;
    return candidato.every((c) =>
      splits.some(
        (s) =>
          s.participant_id === c.participant_id &&
          Math.round(s.percentage * 10000) === Math.round(c.percentage * 10000),
      ),
    );
  };

  const guardar = async () => {
    setError(null);
    if (!orgId || !client) {
      setError('Elegí un cliente');
      return;
    }
    if (items.length === 0) {
      setError('Agregá al menos un honorario');
      return;
    }
    if (lineasIncompletas > 0) {
      setError(
        lineasIncompletas === 1
          ? 'Hay un honorario sin elegir'
          : `Hay ${lineasIncompletas} honorarios sin elegir`,
      );
      return;
    }
    if (!biller) {
      setError('Indicá quién factura');
      return;
    }
    if (!splitsValidos) {
      setError(`El reparto suma ${(sumaSplits / 10000).toFixed(4)} % y debe sumar 100 %`);
      return;
    }

    if (periodoCerrado) {
      setError(
        `El período ${MESES[billingMonth - 1]} ${billingYear} está cerrado. ` +
          'Reabrilo para poder agregar trabajos a ese mes.',
      );
      return;
    }

    const necesitaBeneficiario = mobilityCents !== 0;
    if (necesitaBeneficiario && !mobBeneficiario) {
      setError('Indicá quién cobra la movilidad');
      return;
    }

    const payload: WorkPayload = {
      // Los destinatarios van DENTRO del payload: así viajan con la cola y se
      // escriben en la misma transacción que crea el trabajo, incluso cuando el
      // alta se hizo sin señal y sincroniza horas después.
      invoice_recipients: destinatarios.map((d, i) => ({
        name: d.name,
        percentage: d.percentage,
        sort_order: i,
      })),
      organization_id: orgId,
      client_id: client.id,
      establishment_id: establishment?.id ?? null,
      work_date: workDate,
      billing_year: billingYear,
      billing_month: billingMonth,
      biller_participant_id: biller,
      description: description.trim() || null,
      is_billed: isBilled,
      is_paid: isPaid,
      items: items.map((i, idx) => ({
        organization_service_id: i.organization_service_id,
        service_name: i.service_name,
        calculation: i.calculation,
        kg_per_unit: i.kg_per_unit ?? 0,
        quantity: i.quantity ?? 0,
        sort_order: idx,
      })),
      splits,
      mobility: {
        mode: mobility.mode,
        km: mobility.km,
        rate_cents: mobility.rate_cents,
        amount_cents: mobility.amount_cents,
        allocations: necesitaBeneficiario
          ? [{ participant_id: mobBeneficiario, percentage: 100, sort_order: 1 }]
          : [],
      },
    };

    setGuardando(true);
    try {
      // El duplicado usa el alta normal: es un trabajo GENUINAMENTE NUEVO, con
      // sus propios snapshots y valorizado al precio de su período.
      if (esEdicion && workId) {
        // Editar sin señal no está resuelto: si alguien más tocó el mismo
        // trabajo mientras tanto, habría que decidir quién gana, y esa decisión
        // no se toma sola. Mejor decirlo que fingir que se guardó.
        if (!hayConexion) {
          setError(
            'Para editar un trabajo hace falta conexión. Lo que estás viendo se conserva; ' +
              'probá de nuevo cuando vuelva la señal.',
          );
          return;
        }
        await updateWork(workId, payload);
        await setWorkInvoiceRecipients(workId, destinatarios);
        onSaved({ estado: 'sincronizado', workId });
      } else {
        // Toda alta pasa por la cola, con señal o sin ella. Así el camino es
        // uno solo y el que se usa a diario es el mismo que se probó: si
        // hubiera dos, el de sin señal sería el que nadie ejercita.
        const r = await encolar({
          organizationId: orgId,
          payload: payload as unknown as Record<string, unknown>,
          resumen: {
            cliente: client.name,
            establecimiento: establishment?.name ?? null,
            fecha: workDate,
            servicios: items.map((i) => i.service_name).join(', '),
            // El importe que se veía en pantalla al apretar Guardar.
            totalCents,
            billingYear,
            billingMonth,
          },
        });

        if (r.estado === 'error') {
          setError(r.motivo ?? 'El servidor rechazó el trabajo');
          return;
        }

        // Los honorarios usados quedan a mano para el próximo trabajo.
        for (const i of items) {
          if (i.organization_service_id) registrarReciente(orgId, i.organization_service_id);
        }
        onSaved(
          r.estado === 'sincronizado'
            ? { estado: 'sincronizado', workId: r.workSessionId }
            : { estado: 'pendiente' },
        );
      }
    } catch (e) {
      // Nunca el crudo. Un `TypeError: Failed to fetch` debajo del botón
      // Guardar hace creer que el trabajo se perdió, cuando en realidad quedó
      // en la cola del teléfono.
      setError(mensajeDeError(e, 'El trabajo quedó guardado en este teléfono.'));
    } finally {
      setGuardando(false);
    }
  };

  if (!activeOrg || !orgId) return null;

  if (cargandoTrabajo) {
    return (
      <p className="p-6 text-sm text-[var(--tinta-suave)]" data-testid="work-form-loading">
        Cargando el trabajo…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-4 pb-44" data-testid="work-form">
      <EncabezadoPantalla
        titulo={TITULOS[mode]}
        acciones={
          <Boton jerarquia="plano" onClick={onCancel}>
            Cancelar
          </Boton>
        }
      />

      {bloqueado && (
        <p
          className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
          data-testid="work-locked"
        >
          Este trabajo pertenece a un período cerrado: se puede consultar, pero no modificar.
        </p>
      )}

      {periodoCerrado && !bloqueado && (
        <p
          className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
          data-testid="period-closed"
        >
          <strong>
            El período {MESES[billingMonth - 1]} {billingYear} está cerrado.
          </strong>{' '}
          Elegí otro mes en «Se factura en», o reabrí el cierre desde Liquidación. Mientras tanto no
          se puede cargar nada para ese mes.
        </p>
      )}

      {esDuplicado && (
        <p
          className="rounded-[var(--radius-control)] bg-[var(--superficie-suave)] px-3 py-2 text-sm text-[var(--accion)]"
          data-testid="duplicate-note"
        >
          Se copiaron los datos del trabajo original. El precio se toma del período nuevo
          {lineasModificadas > 0 && (
            <>
              {' '}
              y hay {lineasModificadas === 1
                ? 'un honorario'
                : `${lineasModificadas} honorarios`}{' '}
              con kg distintos del tarifario actual: revisalos abajo
            </>
          )}
          .
        </p>
      )}

      {/* Los datos generales van en su TARJETA, igual que las demás secciones.
          Era el único bloque suelto del formulario —sin fondo ni borde— y por
          eso se fundía con lo que venía después: en el teléfono no se entendía
          dónde terminaba una cosa y empezaba la otra. Todas las secciones se
          ven igual o ninguna. */}
      <section className="flex flex-col gap-3 superficie p-4">
        <EncabezadoSeccion titulo="Generales" />
        {faltanDefaults && client && (
          <p
            className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
            data-testid="faltan-defaults"
          >
            No hay valores habituales guardados para este cliente —movilidad, facturador, reparto—.
            Completalos a mano; el trabajo se guarda igual.
          </p>
        )}

        <label className="flex flex-col gap-1 text-sm">
          Fecha del trabajo
          <input
            type="date"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
            // `w-full` y no una tipografía más chica. El campo de fecha nativo
            // de Android toma su ancho INTRÍNSECO si no se le da uno, y ese
            // ancho no alcanza para `dd/mm/aaaa` a 16 px: en el teléfono se veía
            // «02/09/202», con el año cortado. Achicar la letra lo taparía a
            // costa de que se lea peor justo el dato que define el trabajo.
            className="min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
            data-testid="work-date"
          />
        </label>

        <ClientPicker organizationId={orgId} value={client} onChange={setClient} />

        {client && (
          <EstablishmentPicker
            organizationId={orgId}
            clientId={client.id}
            value={establishment}
            onChange={setEstablishment}
          />
        )}

        <div className="flex flex-col gap-1 text-sm">
          <span>Se factura en</span>
          <div className="flex gap-2">
            <select
              value={billingMonth}
              onChange={(e) =>
                setBillingOverride({ year: billingYear, month: Number(e.target.value) })
              }
              className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
              data-testid="billing-month"
            >
              {MESES.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={billingYear}
              onChange={(e) =>
                setBillingOverride({ year: Number(e.target.value), month: billingMonth })
              }
              className="min-h-11 w-24 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
              data-testid="billing-year"
            />
          </div>
          <span className="text-xs text-[var(--tinta-tenue)]">
            El cierre del mes usa esta fecha, no la del trabajo.
          </span>
        </div>
      </section>

      {/* Con el período cerrado no se muestra nada más que los datos generales.
          Antes el aviso estaba y el formulario seguía abierto: se podían cargar
          seis honorarios y un reparto para enterarse al apretar Guardar. Ver el
          problema y poder seguir haciendo el trabajo igual es peor que no
          avisar, porque promete que va a andar.

          Esto es ayuda, no seguridad: quien rechaza el alta en un período
          cerrado es la base, y lo seguirá haciendo aunque esta pantalla falle. */}
      {/* Con el período cerrado el formulario se pliega, pero lo YA ESCRITO no
          se esconde. Las observaciones son lo único del trabajo que no es un
          número —qué se gastó, por qué se volvió, qué pasó con un animal— y es
          justo lo que se busca al revisar un mes cerrado. Desaparecían junto
          con los controles: no poder EDITAR no es razón para no poder LEER. */}
      {!puedeCargar && (
        <section className="flex flex-col gap-2 superficie p-4" data-testid="observaciones-cerrado">
          <EncabezadoSeccion titulo="Gastos del trabajo" />
          {description.trim() !== '' ? (
            /* `whitespace-pre-wrap`: se escribe en renglones —«1,3 kit», «13
                dosis»— y aplastarlo en un párrafo corrido lo vuelve ilegible
                justo cuando se lo está buscando. */
            <p className="whitespace-pre-wrap text-sm">{description}</p>
          ) : (
            <p className="text-sm text-[var(--tinta-suave)]">
              No se anotó ningún gasto en este trabajo.
            </p>
          )}
        </section>
      )}

      {puedeCargar && (
        <>
          {/* ---------------------------------------------------------------- */}
          {/* Plegable como las demás, pero abierto por omisión: es lo primero
              que se carga. Y si alguna línea quedó a medias, `alerta` lo abre y
              no lo deja cerrar: esconder un error adentro de una sección
              cerrada es la forma más rápida de que se apriete Guardar y no
              pase nada visible. */}
          <Plegable
            titulo="Honorarios"
            resumen={resumenHonorarios}
            alerta={lineasIncompletas > 0}
            defectoAbierto
            testId="seccion-honorarios"
          >
            {items.length === 0 && (
              <p className="text-sm text-[var(--tinta-suave)]">Todavía no agregaste honorarios.</p>
            )}

            {items.map((i) => {
              const kgLinea =
                i.calculation === 'per_job'
                  ? kg3(i.kg_per_unit ?? 0)
                  : kg3((i.quantity ?? 0) * (i.kg_per_unit ?? 0));
              const elegido = Boolean(i.service_name);
              const modificado = elegido && kg3(i.kg_per_unit ?? 0) !== kg3(i.kg_suggested);
              return (
                <div
                  key={i.key}
                  className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3"
                  data-testid="item-row"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ServicePicker
                        organizationId={orgId}
                        services={services}
                        value={i.organization_service_id}
                        fallbackName={i.service_name || undefined}
                        reciénAgregada={i.key === lineaNueva}
                        onChange={(s) => cambiarServicio(i.key, s)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setItems((prev) => prev.filter((x) => x.key !== i.key))}
                      className="min-h-11 shrink-0 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
                      data-testid="item-remove"
                      aria-label="Quitar honorario"
                    >
                      ✕
                    </button>
                  </div>

                  {elegido && (
                    <>
                      <div className="flex gap-2">
                        {i.calculation === 'per_unit' && (
                          <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-[var(--tinta-suave)]">
                            Cantidad
                            <CampoNumero
                              entero
                              min={0}
                              step="1"
                              placeholder="Ej. 300"
                              value={i.quantity}
                              onChange={(n) =>
                                setItems((prev) =>
                                  prev.map((x) => (x.key === i.key ? { ...x, quantity: n } : x)),
                                )
                              }
                              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                              data-testid="item-quantity"
                            />
                          </label>
                        )}
                        <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-[var(--tinta-suave)]">
                          kg por {i.calculation === 'per_job' ? 'trabajo' : 'unidad'}
                          <CampoNumero
                            min={0}
                            step="0.001"
                            placeholder="Ej. 1,5"
                            value={i.kg_per_unit}
                            onChange={(n) =>
                              setItems((prev) =>
                                prev.map((x) => (x.key === i.key ? { ...x, kg_per_unit: n } : x)),
                              )
                            }
                            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                            data-testid="item-kg"
                          />
                        </label>
                      </div>

                      {modificado && (
                        <div
                          className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-2 py-1 text-xs text-[var(--aviso)]"
                          data-testid="item-kg-diff"
                        >
                          <span>Tarifa distinta del tarifario actual ({i.kg_suggested} kg).</span>
                          <button
                            type="button"
                            onClick={() =>
                              setItems((prev) =>
                                prev.map((x) =>
                                  x.key === i.key ? { ...x, kg_per_unit: x.kg_suggested } : x,
                                ),
                              )
                            }
                            className="min-h-8 rounded border border-[var(--aviso)] px-2"
                            data-testid="item-kg-use-current"
                          >
                            Usar {i.kg_suggested} kg
                          </button>
                          <span className="opacity-70">o dejá el valor cargado.</span>
                        </div>
                      )}

                      <p className="text-sm">
                        <strong data-testid="item-subtotal">{kgLinea} kg</strong>
                        {steerPrice !== null && (
                          <span className="opacity-70">
                            {' · '}
                            {formatCents(Math.round(kgLinea * steerPrice))}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={agregarLinea}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed px-4 text-sm"
              data-testid="add-item"
            >
              + Agregar honorario
            </button>
          </Plegable>

          {/* ---------------------------------------------------------------- */}
          <Plegable titulo="Movilidad" resumen={resumenMovilidad} testId="seccion-movilidad">
            {/* Elegir el método era abrir un desplegable de cinco opciones para
            terminar eligiendo, casi siempre, la misma. Se muestran los dos
            atajos que resuelven el caso normal —el habitual de la organización
            y no cobrar— y el resto queda a un toque. Cinco chips fijos ocupaban
            dos filas enteras en un teléfono. */}
            <div className="flex flex-col gap-1 text-sm">
              <span>Método</span>
              <div className="flex flex-wrap gap-2" data-testid="mobility-mode">
                {modoHabitual && modoHabitual !== 'none' && (
                  <Chip
                    activo={mobility.mode === modoHabitual}
                    onClick={() => setMobility({ ...mobility, mode: modoHabitual })}
                    testId="mobility-habitual"
                  >
                    Habitual · {MOBILITY_LABELS[modoHabitual]}
                  </Chip>
                )}
                <Chip
                  activo={mobility.mode === 'none'}
                  onClick={() => setMobility({ ...mobility, mode: 'none' })}
                  testId="mobility-none"
                >
                  No cobrar
                </Chip>
                {!otrosMetodos && (
                  <Chip
                    activo={false}
                    onClick={() => setOtrosMetodos(true)}
                    testId="mobility-otros"
                  >
                    Otros métodos
                  </Chip>
                )}
              </div>

              {otrosMetodos && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {MODES.filter((m) => m !== 'none' && m !== modoHabitual).map((m) => (
                    <Chip
                      key={m}
                      activo={mobility.mode === m}
                      onClick={() => setMobility({ ...mobility, mode: m })}
                      testId={`mobility-modo-${m}`}
                    >
                      {MOBILITY_LABELS[m]}
                    </Chip>
                  ))}
                </div>
              )}
            </div>

            {/* Si no se cobra, no se muestra ningún campo de cálculo */}
            {(mobility.mode === 'per_km_fuel' || mobility.mode === 'per_km_fixed') && (
              <label className="flex flex-col gap-1 text-sm">
                Kilómetros
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={mobility.km ?? ''}
                  onChange={(e) =>
                    setMobility({
                      ...mobility,
                      km: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                  data-testid="mobility-km"
                />
              </label>
            )}

            {mobility.mode === 'per_km_fixed' && (
              <label className="flex flex-col gap-1 text-sm">
                Tarifa por km
                <input
                  inputMode="decimal"
                  value={mobility.rate_cents !== null ? String(mobility.rate_cents / 100) : ''}
                  onChange={(e) =>
                    setMobility({ ...mobility, rate_cents: parsePesosToCents(e.target.value) })
                  }
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                  data-testid="mobility-rate"
                />
              </label>
            )}

            {(mobility.mode === 'flat' || mobility.mode === 'manual') && (
              <label className="flex flex-col gap-1 text-sm">
                Importe
                <input
                  inputMode="decimal"
                  value={mobility.amount_cents !== null ? String(mobility.amount_cents / 100) : ''}
                  onChange={(e) =>
                    setMobility({ ...mobility, amount_cents: parsePesosToCents(e.target.value) })
                  }
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                  data-testid="mobility-amount"
                />
              </label>
            )}

            {mobility.mode !== 'none' && mobilityCents !== 0 && !soloUno && (
              <label className="flex flex-col gap-1 text-sm">
                La cobra
                <select
                  value={mobBeneficiario}
                  onChange={(e) => setMobBeneficiario(e.target.value)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
                  data-testid="mobility-beneficiary"
                >
                  <option value="">Elegir…</option>
                  {participants.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <p className="text-sm" data-testid="mobility-amount-preview">
              Movilidad: <strong>{formatCents(mobilityCents)}</strong>
              {mobility.mode === 'per_km_fuel' && fuelPrice === null && (mobility.km ?? 0) > 0 && (
                <span className="ml-2 text-xs text-[var(--tinta-suave)]">
                  falta el precio del combustible
                </span>
              )}
            </p>
          </Plegable>

          {/* ---------------------------------------------------------------- */}
          {!soloUno && (
            <Plegable
              titulo="Economía"
              resumen={resumenEconomia}
              alerta={!splitsValidos && splits.length > 0}
              testId="seccion-economia"
            >
              <label className="flex flex-col gap-1 text-sm">
                Quién factura
                <select
                  value={biller}
                  onChange={(e) => setBiller(e.target.value)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
                  data-testid="biller"
                >
                  <option value="">Elegir…</option>
                  {participants.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-[var(--tinta-tenue)]">
                  Quien factura es quien recibe el pago.
                </span>
              </label>

              <div className="flex flex-col gap-2">
                <span className="text-sm">Distribución de la ganancia</span>

                {/* Acciones rápidas: el reparto habitual en un toque, sin escribir
                porcentajes ni resolver restos a mano. */}
                <div className="flex flex-wrap gap-2" data-testid="split-quick-actions">
                  {participants.map((p) => {
                    const activo = repartoActualEs([
                      { participant_id: p.id, percentage: 100, sort_order: 1 },
                    ]);
                    // Estos tres eran botones sueltos con el mismo dibujo copiado a
                    // mano, y por eso quedaron fuera del arreglo del componente: el
                    // seleccionado era indistinguible justo acá, donde elegir mal el
                    // reparto de un trabajo no avisa.
                    return (
                      <Chip
                        key={p.id}
                        activo={activo}
                        onClick={() => repartoParaUno(p.id)}
                        testId={`split-all-${p.display_name}`}
                      >
                        100 % {p.display_name}
                      </Chip>
                    );
                  })}

                  {participants.length > 1 && (
                    <Chip
                      activo={repartoActualEs(
                        fromSplitLines(buildEvenSplit(participants.map((p) => p.id))),
                      )}
                      onClick={repartoParaTodos}
                      testId="split-even"
                    >
                      {/* Con dos participantes, «partes iguales» ES el 50 / 50 que
                      el usuario tiene en la cabeza. */}
                      {participants.length === 2 ? '50 / 50' : 'Partes iguales'}
                    </Chip>
                  )}

                  {templates.map((t) => (
                    <Chip
                      key={t.id}
                      activo={repartoActualEs(t.lines)}
                      onClick={() => aplicarPlantilla(t)}
                      testId="split-template"
                    >
                      {t.name}
                    </Chip>
                  ))}

                  <button
                    type="button"
                    onClick={() => setRepartoManual((v) => !v)}
                    aria-expanded={repartoManual}
                    className="min-h-11 rounded-full border border-dashed border-[var(--linea-fuerte)] px-3 text-sm"
                    data-testid="split-custom"
                  >
                    Personalizado
                  </button>
                </div>

                {repartoManual &&
                  participants.map((p) => {
                    const linea = splits.find((s) => s.participant_id === p.id);
                    return (
                      <div key={p.id} className="flex items-center gap-2" data-testid="split-row">
                        <span className="flex-1 text-sm">{p.display_name}</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.0001"
                          inputMode="decimal"
                          value={linea?.percentage ?? ''}
                          placeholder="0"
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : Number(e.target.value);
                            setSplits((prev) => {
                              const resto = prev.filter((s) => s.participant_id !== p.id);
                              if (v <= 0) return resto;
                              return [
                                ...resto,
                                {
                                  participant_id: p.id,
                                  percentage: v,
                                  sort_order: participants.indexOf(p) + 1,
                                },
                              ].sort((a, b) => a.sort_order - b.sort_order);
                            });
                          }}
                          className="min-h-11 w-24 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                          data-testid={`split-${p.display_name}`}
                        />
                        <span className="text-sm text-[var(--tinta-suave)]">%</span>
                      </div>
                    );
                  })}

                <p
                  className={`text-sm ${splitsValidos ? 'text-[var(--exito)]' : 'text-[var(--peligro)]'}`}
                  data-testid="split-total"
                >
                  {splits.length === 0
                    ? 'Elegí cómo se reparte'
                    : `${splits
                        .map((s) => `${nombreDe(s.participant_id)} ${s.percentage} %`)
                        .join(' · ')}`}{' '}
                  {splitsValidos
                    ? '✓'
                    : `(suma ${(sumaSplits / 10000).toFixed(4).replace(/\.?0+$/, '')} %) ✗`}
                </p>
              </div>
            </Plegable>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Facturado y cobrado se fueron de acá.
          Nadie termina un trabajo en el campo y lo cobra en el mismo minuto:
          son dos momentos distintos, y pedirlos mientras se carga la visita
          obligaba a contestar dos preguntas cuya respuesta siempre es «todavía
          no». Se marcan desde el listado, tocando la tarjeta, que es cuando de
          verdad pasa.

          Lo que sí se escribe en el campo —los insumos, las dosis, el kit y
          medio— se queda: es el dato que después no está en ningún otro lado. */}
          {/* Sólo aparece si el cliente tiene reparto o si se agregó uno acá: para
          un cliente normal —una factura, un nombre— no hay nada que decidir y
          la sección sería ruido en el noventa por ciento de las cargas. */}
          {(destinatarios.length > 0 || client) && (
            <Plegable
              titulo="Facturar a"
              resumen={
                destinatarios.length === 0
                  ? (client?.name ?? 'El cliente')
                  : destinatarios.map((d) => `${d.name} ${d.percentage} %`).join(' · ')
              }
              testId="seccion-destinatarios"
            >
              <p className="text-xs text-[var(--tinta-tenue)]">
                A nombre de quién se emite la factura. Se hereda del cliente y se guarda con este
                trabajo: cambiarlo después en el cliente no altera los trabajos ya cargados.
              </p>
              <RepartoFactura
                lineas={destinatarios}
                onChange={setDestinatarios}
                totalCents={totalCents}
              />
            </Plegable>
          )}

          <Plegable
            titulo="Gastos del trabajo"
            resumen={description.trim() === '' ? 'Sin gastos anotados' : description.trim()}
            testId="seccion-observaciones"
          >
            <label className="flex flex-col gap-1 text-sm">
              Qué se gastó en el campo
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="1 cobre, 2 dexa, 0,5 lido…"
                className="rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3 text-base"
                data-testid="description"
              />
            </label>
          </Plegable>
        </>
      )}

      {/* El bloque «Resumen» que estaba acá se eliminó: la barra fija de abajo
          muestra exactamente el mismo desglose al desplegarla. Tenerlo dos
          veces costaba 188 px de scroll y no agregaba un dato. */}

      {/* ------------------------------------------------------------------
          El cierre del formulario: cuánto da y el botón de guardar.

          Esto era una barra FIJA contra el borde de abajo. La idea era tener el
          total siempre a la vista; el efecto real en un teléfono fue otro: te
          seguía todo el tiempo, y con el teclado abierto se comía la franja que
          quedaba justo cuando estabas escribiendo un número. Un total que
          estorba mientras cargás no es información, es un obstáculo.

          Ahora está donde termina la carga. Se llega scrolleando, que es lo que
          se hace igual antes de guardar, y cuando aparece ya están todos los
          datos: el número que muestra es el definitivo, no uno a medio hacer.
         ------------------------------------------------------------------ */}
      <div className="flex flex-col gap-3 superficie p-4" data-testid="cierre-formulario">
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex justify-between">
            <span className="opacity-70">Honorarios</span>
            <span>{formatCents(honorariaCents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="opacity-70">Movilidad</span>
            <span>{formatCents(mobilityCents)}</span>
          </div>
          {!soloUno &&
            repartoPreview &&
            [...repartoPreview.entries()].map(([pid, cents]) => (
              <div key={pid} className="flex justify-between">
                <span className="opacity-70">{nombreDe(pid)}</span>
                <span>{formatCents(cents)}</span>
              </div>
            ))}
          <div className="mt-1 flex justify-between border-t border-[var(--linea)] pt-2 font-semibold">
            <span data-testid="sticky-kg">{totalKg} kg</span>
            <span
              className={totalCents === null ? 'text-[var(--aviso)]' : ''}
              data-testid="sticky-total"
            >
              {formatCents(totalCents)}
            </span>
          </div>
        </div>

        {/* El error vive pegado al botón que lo dispara. Cuando estaba arriba,
            en otra sección, se apretaba Guardar y no pasaba nada visible. */}
        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-[var(--peligro-fondo)] px-3 py-2 text-sm text-[var(--peligro)]"
            data-testid="work-error"
          >
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando || bloqueado || periodoCerrado}
          className="min-h-14 w-full rounded-[var(--radius-control)] bg-[var(--accion)] text-base font-medium text-[var(--sobre-accion)] disabled:opacity-50"
          data-testid="save-work"
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
